<!-- svelte-ignore a11y-autofocus -->
<script>
    import { afterUpdate } from 'svelte';
    import clsx from 'clsx';
    import clickOutside from 'svelte-outside-click';
    import Emoji from '../../Emoji.svelte';
    import StatusIcon from './StatusIcon.svelte';

    // Utils
    import {
        dateInputsEmpty,
        formatDateMonthInput,
        formatYear,
        fullDateEntered,
        getDateFormatObject,
        validateDateInput,
    } from '../helper';
    import { getBorderRadius } from '$lib/global/helper/border-radius';

    export let placeholder = '';
    export let customOptions = {
        dateFormat: '',
    };
    export let showEmoji = false;
    export let emoji = '';
    export let required = false;
    export let pristine = true;
    export let touched = false;
    export let isFirst = false;
    export let isLast = false;
    export let finalFormInput = {};
    export let errorMessage = '';

    // Custom meta
    let invalid = false;

    let showError = invalid && touched;
    let showSuccess = !invalid && touched && !pristine;
    let showCheckmark = true;

    // Styling
    let wrapperClasses;
    let inputClasses;
    let inputWrapperClasses;
    let placeholderClasses;
    let separatorClasses;
    let dateInputClasses;

    // Input values
    let inputValues = {
        day: '',
        month: '',
        year: '',
    };

    let prevValues = {
        day: '',
        month: '',
        year: '',
    };

    const dateFormatObject = getDateFormatObject(customOptions.dateFormat);
    let editing = false;

    let globallyFocused = false; // Aids global input styling
    let skipGlobalFocus = false; // Aids Tab travel between fields
    let skipFocusCatcher = false; // Aids Shift + Tab travel between fields
    let highlightSeparator = false; // Aids separator styling

    // Refs
    let globalInputRef;
    const orderedRefs = {
        [dateFormatObject.day]: undefined,
        [dateFormatObject.month]: undefined,
        [dateFormatObject.year]: undefined,
    };

    const handleValidDateEntered = (dateSelected) => {
        const dateTimeStamp = dateSelected.toISOString();
        globalInputRef.value = dateTimeStamp;
        finalFormInput.onChange(dateTimeStamp);
    };

    const handleEmptyDateEntered = () => {
        if (globalInputRef.value !== '') {
            globalInputRef.value = '';
            finalFormInput.onChange('');
        }
    };

    const handleInvalidDateEntered = () => {
        if (globalInputRef.value !== 'invalid') {
            globalInputRef.value = 'invalid';
            finalFormInput.onChange('invalid');
        }
    };

    afterUpdate(() => {
        let dateSelected;
        let isFullDateEntered = fullDateEntered(inputValues);
        let areDateInputsEmpty = dateInputsEmpty(inputValues);

        highlightSeparator = !areDateInputsEmpty;

        // Reset validation on empty
        if (areDateInputsEmpty) {
            invalid = required && pristine && touched;
        }

        // Validate input if full date has been entered
        if (isFullDateEntered) {
            invalid = validateDateInput(inputValues, touched);

            const dateSelectedUTCTimestamp = !invalid
                ? Date.UTC(inputValues.year, inputValues.month - 1, inputValues.day)
                : undefined;

            dateSelected = dateSelectedUTCTimestamp
                ? new Date(dateSelectedUTCTimestamp)
                : undefined;
        } else if (!areDateInputsEmpty) {
            invalid = true;
        }

        // Assign value to global input if date is valid
        if (!invalid && !isNaN(dateSelected)) {
            handleValidDateEntered(dateSelected);
        } else if (areDateInputsEmpty) {
            handleEmptyDateEntered();
        } else if (invalid) {
            handleInvalidDateEntered();
        }

        showError =
            (invalid && (touched || isFullDateEntered)) || (required && pristine && touched);
        showSuccess = !invalid && !pristine && isFullDateEntered;
        showCheckmark = true;
    });

    const handleBlur = (e, inputField, formatInput) => {
        skipGlobalFocus = false;
        skipFocusCatcher = false;

        // Handle single digit in day/month
        if (formatInput && e.target.value.length === 1) {
            inputValues[inputField] = `0${e.target.value}`;
        }
    };

    const handleClick = (e) => {
        e.stopPropagation();
    };

    const handleFocus = (e) => {
        skipFocusCatcher = true;
    };

    const handleGlobalClick = () => {
        enableEditing();
        focusOnInput(1, false);
    };

    const enableEditing = () => {
        editing = true;
        globallyFocused = true;
    };

    const handleKeyDown = (event, inputOrder) => {
        const travelBack = event.shiftKey && event.key === 'Tab';
        const canTravel = travelBack ? inputOrder > 1 : inputOrder < 3;
        const inputHighlighted = event.target.selectionStart !== event.target.selectionEnd;
        const caretAtInputStart = event.target.selectionStart === 0;
        const caretAtInputEnd =
            event.target.selectionStart === Number(event.target.attributes.maxlength.value);

        // Handle Tab travel
        if (event.key === 'Tab' && canTravel) {
            event.preventDefault();
            focusNextInput(inputOrder, travelBack, true);
        } else if (travelBack && !canTravel) {
            skipGlobalFocus = true;
        }

        // Handle focused state
        if (event.key === 'Tab' && !canTravel) {
            globallyFocused = false;
            closeIfEmpty();
        }

        // Handle Backspace
        if (
            event.key === 'Backspace' &&
            !inputHighlighted &&
            (!event.target.value.length || caretAtInputStart)
        ) {
            focusNextInput(inputOrder, true, false, true);
        }

        // Handle Arrow travel
        if (event.key === 'ArrowLeft' && caretAtInputStart) {
            event.preventDefault();
            focusNextInput(inputOrder, true);
        } else if (event.key === 'ArrowRight' && caretAtInputEnd) {
            event.preventDefault();
            focusNextInput(inputOrder);
        }
    };

    const handleGlobalFocus = () => {
        if (skipGlobalFocus) {
            skipGlobalFocus = false;
            return;
        }

        enableEditing();
        focusOnInput(1, true);
    };

    const handleFocusCatcher = () => {
        enableEditing();
        focusOnInput(3, !!orderedRefs[3].value);
    };

    const closeIfEmpty = () => {
        handleTouched();

        if (dateInputsEmpty(inputValues)) {
            editing = false;
            invalid = required && touched;
        } else {
            // Validate on global blur
            invalid = validateDateInput(inputValues, touched);
        }
    };

    const handleClickOutside = () => {
        if (!editing) return;

        closeIfEmpty();
        globallyFocused = false;
    };

    const focusNextInput = (inputOrder, descending, selectInput, startAtInputEnd) => {
        const nextInputOrder = descending ? inputOrder - 1 : inputOrder + 1;
        focusOnInput(nextInputOrder, selectInput, startAtInputEnd);
    };

    const focusOnInput = (order, selectInput, startAtInputEnd) => {
        const inputToFocus = orderedRefs[order];
        if (inputToFocus) {
            inputToFocus.focus();

            if (selectInput) {
                inputToFocus.value && inputToFocus.select();
            } else if (startAtInputEnd) {
                inputToFocus.selectionStart = Number(inputToFocus.attributes.maxlength.value);
            }
        }
    };

    const handlePristine = () => {
        pristine = !!dateInputsEmpty(inputValues);
    };

    const handleTouched = () => {
        if (!touched) {
            touched = true;
        }
    };

    const handleMeta = () => {
        handlePristine();
    };

    const handleInputEnd = (fieldOrder, inputLength, maxLength) => {
        // Focus on next input if field is full
        if (inputLength === maxLength) {
            focusNextInput(fieldOrder, false, true);
        }
    };

    const validateDateMonth = (inputField) => {
        const formattedInput = formatDateMonthInput(
            inputField,
            inputValues[inputField],
            prevValues[inputField],
        );

        inputValues[inputField] = formattedInput;
        prevValues[inputField] = formattedInput;

        handleInputEnd(dateFormatObject[inputField], formattedInput.length, 2);
        handleMeta();
    };

    const validateYear = () => {
        const formattedInput = formatYear(inputValues.year, prevValues['year']);
        inputValues.year = formattedInput;
        prevValues.year = formattedInput;

        handleInputEnd(dateFormatObject.year, formattedInput.length, 4);
        handleMeta();
    };

    const borderRadius = getBorderRadius('fieldsWrapper');

    $: wrapperClasses = clsx(borderRadius, 'wrapper flex relative box-border overflow-hidden', {
        ['rounded-b-none']: !isLast,
        ['rounded-t-none']: !isFirst,
        ['border-b']: isFirst && isLast,
        invalid: invalid || showError,
    });

    $: inputWrapperClasses = clsx(
        borderRadius,
        'transition-all input flex flex-row box-border w-full px-6 border-0 border-l border-r border-t border-black/10 mb-0 font-ui text-base bg-white font-light rounded-none cursor-text',
        {
            ['bg-gray-50 outline-none']: globallyFocused,
            ['rounded-b-none']: !isLast,
            ['rounded-t-none']: !isFirst,
            ['border-b']: ((invalid && isFirst) || !isFirst) && isLast,
            ['pl-[70px]']: showEmoji,
            ['h-[90px]']: invalid && showError,
            ['h-[64px]']: !invalid || !showError,
            ['pr-24']: !required && pristine,
        },
    );

    $: inputClasses = (order, isYear) =>
        clsx(
            borderRadius,
            `atomic-input text-left z-10 placeholder:text-gray-300 placeholder:font-mono placeholder:font-light shadow-none font-ui text-base font-light focus:outline-none transition`,
            {
                'w-10': isYear,
                'w-5': !isYear,
                'order-1 ml-0': order === 1,
                'order-3': order === 2,
                'order-5 mr-0': order === 3,
                'bg-gray-50': globallyFocused,
            },
        );

    $: placeholderClasses = clsx(
        'mt-5 placeholder text-gray-400 z-20 text-base font-light truncate',
        {
            'z-0 hidden': editing,
        },
    );

    $: separatorClasses = (order) =>
        clsx('font-light', {
            'text-black': highlightSeparator && !invalid,
            'text-gray-300': !highlightSeparator && !invalid,
            'order-2': order === 1,
            'order-4': order === 2,
        });

    $: dateInputClasses = clsx('flex flex-row absolute top-0 bottom-0 mt-5 h-fit', {
        'opacity-0 pointer-events-none': !editing,
        'opacity-100': editing,
        'left-6': !showEmoji,
        'left-[70px]': showEmoji,
    });
</script>

<div class={wrapperClasses}>
    {#if showEmoji}
        <u class="absolute left-6 mt-6 flex h-[18px] select-none items-center no-underline">
            <Emoji {emoji} size="24px" />
        </u>
    {/if}

    {#if !required && !invalid && pristine}
        <u class="absolute right-6 mt-6 text-xs font-light text-gray-500 no-underline">(optional)</u
        >
    {/if}

    <StatusIcon {showCheckmark} {showError} {showSuccess} />

    <div
        class={inputWrapperClasses}
        on:click={handleGlobalClick}
        use:clickOutside={handleClickOutside}
        on:keydown={showError}
    >
        <input
            type="text"
            name={finalFormInput.name}
            class="absolute -z-10 opacity-0"
            bind:this={globalInputRef}
            on:focus={handleGlobalFocus}
            tabindex={skipGlobalFocus ? -1 : 0}
        />
        <p class={placeholderClasses}>
            {placeholder}
        </p>
        <div class={dateInputClasses}>
            <input
                id="day"
                type="text"
                pattern="\d*"
                class={`${inputClasses(dateFormatObject.day)}`}
                placeholder="dd"
                maxlength="2"
                tabindex="-1"
                bind:this={orderedRefs[dateFormatObject.day]}
                bind:value={inputValues.day}
                on:blur={(e) => handleBlur(e, 'day', true)}
                on:focus={handleFocus}
                on:input={() => validateDateMonth('day')}
                on:keydown={(e) => handleKeyDown(e, dateFormatObject.day)}
                on:click={handleClick}
            />
            <span class={separatorClasses(1)}>{dateFormatObject.separator}</span>
            <input
                id="month"
                type="text"
                pattern="\d*"
                class={`${inputClasses(dateFormatObject.month)}`}
                placeholder="mm"
                maxlength="2"
                tabindex="-1"
                bind:this={orderedRefs[dateFormatObject.month]}
                bind:value={inputValues.month}
                on:blur={(e) => handleBlur(e, 'month', true)}
                on:focus={handleFocus}
                on:input={() => validateDateMonth('month')}
                on:keydown={(e) => handleKeyDown(e, dateFormatObject.month)}
                on:click={handleClick}
            />
            <span class={separatorClasses(2)}>{dateFormatObject.separator}</span>
            <input
                id="year"
                type="text"
                pattern="\d*"
                class={`${inputClasses(dateFormatObject.year, true)}`}
                placeholder="yyyy"
                maxlength="4"
                tabindex="-1"
                bind:this={orderedRefs[dateFormatObject.year]}
                bind:value={inputValues.year}
                on:blur={handleBlur}
                on:focus={handleFocus}
                on:input={validateYear}
                on:keydown={(e) => handleKeyDown(e, dateFormatObject.year)}
                on:click={handleClick}
            />
        </div>

        {#if errorMessage && showError}
            <div class="absolute top-14 text-xs font-normal leading-tight text-[#ef444a]">
                {errorMessage}
            </div>
        {/if}

        <input
            type="text"
            class="absolute -z-20 opacity-0"
            on:focus={handleFocusCatcher}
            tabindex={skipFocusCatcher ? -1 : 0}
        />
    </div>
</div>

<style lang="scss">
    .wrapper {
        box-shadow: 0 1px 1px 0 rgba(#000, 0.1);

        :global([placeholder]) {
            @apply truncate;
        }

        input {
            -webkit-tap-highlight-color: transparent;
        }
    }
</style>
